import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Check, History, Loader2, Pencil, Printer, Send, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";
const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
  sent: "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold",
  accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  rejected: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  cancelled: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  expired: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
};
const money = (cur, v) => `${cur} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const idr = (v) => money("IDR", v);
const API_URL = process.env.REACT_APP_BACKEND_URL;
const BTN = "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors";

export default function QuotationDetail() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("quotations.edit");
  const canCreate = hasPermission("quotations.create");
  const canApprove = hasPermission("quotations.approve");
  const [q, setQ] = useState(null);
  const [doc, setDoc] = useState(null);
  const [tab, setTab] = useState("internal");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/quotations/${quotationId}`);
      setQ(r.data);
      try { setDoc((await api.get(`/quotations/${quotationId}/document`)).data); } catch { setDoc(null); }
    } catch (e) { toast.error(apiError(e)); navigate("/quotations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, [quotationId]); // eslint-disable-line

  const act = async (action, label) => {
    setBusy(true);
    try {
      await api.post(`/quotations/${quotationId}/${action}`);
      toast.success(label);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>;
  if (!q) return null;
  const st = q.status;

  return (
    <div className="max-w-5xl space-y-6" data-testid="quotation-detail-page">
      <button onClick={() => navigate("/quotations")} data-testid="back-to-quotations" className="no-print flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-ink-gold">
        <ArrowLeft className="h-4 w-4" /> Back to Quotations
      </button>

      <div className="no-print flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono-tf text-xs font-semibold text-slate-500 dark:text-ink-gold" data-testid="quotation-detail-number">{q.revision_label}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[q.display_status]}`} data-testid="quotation-detail-status">{q.display_status}</span>
            {!q.is_current && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 dark:bg-ink-panel" data-testid="superseded-badge">superseded</span>}
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text" data-testid="quotation-customer-name">{q.customer_name || "Quotation"}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Inquiry <button onClick={() => navigate(`/inquiries/${q.inquiry_id}`)} className="font-mono-tf text-sky-600 hover:underline dark:text-ink-gold" data-testid="quotation-inquiry-link">{q.inquiry_number}</button>
            {" · "}Costing v{q.costing_version} · Valid until {q.valid_until || "—"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {st === "draft" && canEdit && (
            <button onClick={() => setEditOpen(true)} disabled={busy} data-testid="edit-quotation-button" className={`${BTN} border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel`}><Pencil className="h-4 w-4" /> Edit</button>
          )}
          {st === "draft" && canEdit && (
            <button onClick={() => act("send", "Quotation marked as sent")} disabled={busy} data-testid="send-quotation-button" className={`${BTN} bg-sky-600 text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep`}><Send className="h-4 w-4" /> Mark as Sent</button>
          )}
          {st === "sent" && canApprove && (
            <button onClick={() => act("accept", "Quotation accepted")} disabled={busy} data-testid="accept-quotation-button" className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-500`}><Check className="h-4 w-4" /> Accept</button>
          )}
          {st === "sent" && canEdit && (
            <button onClick={() => setRejectOpen(true)} disabled={busy} data-testid="reject-quotation-button" className={`${BTN} border border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-500/30 dark:hover:bg-rose-500/10`}><X className="h-4 w-4" /> Reject</button>
          )}
          {(st === "draft" || st === "sent") && canEdit && (
            <button onClick={() => act("cancel", "Quotation cancelled")} disabled={busy} data-testid="cancel-quotation-button" className={`${BTN} border border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-ink-line dark:text-slate-400 dark:hover:bg-ink-panel`}><Ban className="h-4 w-4" /> Cancel</button>
          )}
          {["sent", "accepted", "rejected"].includes(st) && q.is_current && canCreate && (
            <button onClick={() => setReviseOpen(true)} disabled={busy} data-testid="revise-quotation-button" className={`${BTN} border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel`}><History className="h-4 w-4" /> New Revision</button>
          )}
        </div>
      </div>

      {(q.revisions || []).length > 1 && (
        <div className="no-print flex flex-wrap items-center gap-2" data-testid="revision-list">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400">Revisions</span>
          {q.revisions.map((r) => (
            <button key={r.id} onClick={() => navigate(`/quotations/${r.id}`)} data-testid={`revision-chip-${r.revision_no}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${r.id === quotationId ? "bg-sky-600 text-white dark:bg-ink-gold dark:text-[#0B1120]" : "border border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-ink-line dark:text-slate-400 dark:hover:bg-ink-panel"}`}>
              Rev.{r.revision_no} · {r.display_status}{r.is_current ? " · current" : " · superseded"}
            </button>
          ))}
        </div>
      )}

      <div className="no-print flex gap-1 rounded-lg border border-slate-200 bg-white p-1 dark:border-ink-line dark:bg-ink-card" data-testid="quotation-tabs">
        {[["internal", "Internal View"], ["customer", "Customer Document"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-sky-600 text-white dark:bg-ink-gold dark:text-[#0B1120]" : "text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-ink-panel"}`}>{l}</button>
        ))}
      </div>

      {tab === "internal" ? (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="commercial-terms">
              <h2 className="mb-4 font-display font-semibold text-slate-900 dark:text-ink-text">Commercial Terms</h2>
              {[["Customer currency", q.currency, "terms-currency"],
                ["Quotation FX rate", q.currency === "IDR" ? "—" : `1 ${q.currency} = ${idr(q.fx_rate)}`, "terms-fx-rate"],
                ["Customer total", money(q.currency, q.customer_total), "terms-customer-total"],
                ["Valid until", q.valid_until || "—", "terms-valid-until"]].map(([l, v, tid]) => (
                <div key={tid} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">{l}</span>
                  <span className="font-mono-tf font-semibold text-slate-800 dark:text-ink-text" data-testid={tid}>{v}</span>
                </div>
              ))}
              {q.notes && <div className="mt-3"><label className={LABEL}>Customer notes</label><p className="text-sm text-slate-600 dark:text-slate-300" data-testid="terms-notes">{q.notes}</p></div>}
              {q.internal_notes && <div className="mt-3"><label className={LABEL}>Internal notes</label><p className="text-sm text-slate-600 dark:text-slate-300" data-testid="terms-internal-notes">{q.internal_notes}</p></div>}
              {q.rejection_reason && <div className="mt-3"><label className={LABEL}>Rejection reason</label><p className="text-sm text-rose-600 dark:text-rose-400" data-testid="terms-rejection-reason">{q.rejection_reason}</p></div>}
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="internal-costing-snapshot">
              <h2 className="mb-1 font-display font-semibold text-slate-900 dark:text-ink-text">Costing Snapshot <span className="text-xs font-normal text-slate-400">(internal · from costing v{q.costing_version})</span></h2>
              <p className="mb-3 text-xs text-slate-400">Frozen at creation — later costing edits do not change this revision.</p>
              {[["Total cost", q.total_cost_idr, "internal-total-cost"],
                ["Markup", q.markup_amount, "internal-markup-amount"],
                ["Selling price (IDR base)", q.selling_price_idr, "internal-selling-price"],
                ["Gross profit", q.gross_profit, "internal-gross-profit"]].map(([l, v, tid]) => (
                <div key={tid} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">{l}</span>
                  <span className="font-mono-tf font-semibold text-slate-800 dark:text-ink-text" data-testid={tid}>{idr(v)}</span>
                </div>
              ))}
              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 dark:border-ink-line">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Gross margin</span>
                <span className="font-mono-tf text-lg font-bold text-emerald-600 dark:text-emerald-400" data-testid="internal-gross-margin">{Number(q.gross_margin_percent || 0).toFixed(2)}%</span>
              </div>
            </section>
          </div>

          <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="quotation-items">
            <h2 className="mb-4 font-display font-semibold text-slate-900 dark:text-ink-text">Included Services</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quotation-items-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500 dark:border-ink-line">
                    <th className="px-2 py-2">Category</th><th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2">Qty</th><th className="px-2 py-2">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.items || []).length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-sm text-slate-400">No service lines.</td></tr>}
                  {(q.items || []).map((it, idx) => (
                    <tr key={idx} className="border-b border-slate-100 dark:border-ink-line" data-testid={`quotation-item-${idx}`}>
                      <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{it.category}</td>
                      <td className="px-2 py-2 text-slate-800 dark:text-ink-text">{it.description || "—"}</td>
                      <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{it.quantity}</td>
                      <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{it.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <div className="space-y-4">
          <div className="no-print flex justify-end">
            <button onClick={() => window.print()} data-testid="print-quotation-button" className={`${BTN} bg-sky-600 text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep`}><Printer className="h-4 w-4" /> Print</button>
          </div>
          {doc ? <CustomerDocument doc={doc} /> : <p className="text-sm text-slate-400">Customer document unavailable.</p>}
        </div>
      )}

      {editOpen && <TermsModal title="Edit Quotation" q={q} onClose={() => setEditOpen(false)}
        onSubmit={async (payload) => { await api.patch(`/quotations/${q.id}`, payload); toast.success("Quotation updated"); setEditOpen(false); load(); }} />}
      {reviseOpen && <TermsModal title={`New Revision of ${q.quotation_number}`} q={q} isRevise onClose={() => setReviseOpen(false)}
        onSubmit={async (payload) => { const r = await api.post(`/quotations/${q.id}/revise`, payload); toast.success(`Revision ${r.data.revision_label} created`); setReviseOpen(false); navigate(`/quotations/${r.data.id}`); }} />}
      {rejectOpen && <RejectModal onClose={() => setRejectOpen(false)} onSubmit={async (reason) => {
        await api.post(`/quotations/${q.id}/reject`, { reason: reason || undefined });
        toast.success("Quotation rejected"); setRejectOpen(false); load();
      }} />}
    </div>
  );
}

function CustomerDocument({ doc }) {
  const c = doc.company || {};
  const t = doc.trip || {};
  return (
    <div id="quotation-document" data-testid="customer-document" className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-slate-900 shadow-sm print:border-0 print:shadow-none">
      <div className="flex items-start justify-between gap-6">
        <div>
          {c.logo_url && <img src={`${API_URL}${c.logo_url}`} alt={c.brand_name || "logo"} className="mb-3 h-10 object-contain" />}
          <div className="font-display text-xl font-bold" data-testid="doc-company-name">{c.brand_name}</div>
          {c.legal_name && <div className="text-xs text-slate-500">{c.legal_name}</div>}
          {c.address && <div className="mt-1 text-xs text-slate-500">{c.address}</div>}
          <div className="mt-1 text-xs text-slate-500">{[c.phone, c.email, c.website].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-extrabold tracking-tight">QUOTATION</div>
          <div className="font-mono-tf text-sm font-semibold" data-testid="doc-number">{doc.revision_label}</div>
          <div className="mt-1 text-xs text-slate-500">Issued {String(doc.issued_at || "").slice(0, 10)}</div>
          <div className="text-xs text-slate-500" data-testid="doc-valid-until">Valid until {doc.valid_until || "—"}</div>
        </div>
      </div>

      <div className="my-6 h-px bg-slate-200" />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Prepared for</div>
          <div className="mt-1 text-sm font-semibold" data-testid="doc-customer-name">{doc.customer?.name}</div>
          <div className="text-xs text-slate-500">{doc.customer?.customer_id}</div>
          <div className="text-xs text-slate-500">{[doc.customer?.email, doc.customer?.phone].filter(Boolean).join(" · ")}</div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Trip summary</div>
          <div className="mt-1 text-sm" data-testid="doc-trip-summary">
            {(t.destinations || []).join(", ") || "—"}
            {t.travel_start_date && ` · ${String(t.travel_start_date).slice(0, 10)} → ${String(t.travel_end_date || "").slice(0, 10)}`}
            {t.num_travelers ? ` · ${t.num_travelers} pax` : ""}
            {t.trip_type ? ` · ${t.trip_type}` : ""}
          </div>
        </div>
      </div>

      <table className="mt-6 w-full text-sm" data-testid="doc-items-table">
        <thead>
          <tr className="border-b border-slate-300 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Service</th>
            <th className="py-2 pr-2 text-right">Qty</th><th className="py-2">Unit</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((it, idx) => (
            <tr key={idx} className="border-b border-slate-100" data-testid={`doc-item-${idx}`}>
              <td className="py-2 pr-2 text-slate-400">{idx + 1}</td>
              <td className="py-2 pr-2"><span className="font-medium">{it.category}</span>{it.description ? ` — ${it.description}` : ""}</td>
              <td className="py-2 pr-2 text-right">{it.quantity}</td>
              <td className="py-2 text-slate-500">{it.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 flex justify-end">
        <div className="w-64 rounded-lg bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total</span>
            <span className="font-mono-tf text-lg font-extrabold" data-testid="doc-total">{money(doc.currency, doc.customer_total)}</span>
          </div>
          {doc.fx_rate && <div className="mt-1 text-right text-[11px] text-slate-400" data-testid="doc-fx-note">Converted from IDR at 1 {doc.currency} = {idr(doc.fx_rate)}</div>}
        </div>
      </div>

      {doc.notes && <div className="mt-6"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Notes</div><p className="mt-1 whitespace-pre-wrap text-sm text-slate-600" data-testid="doc-notes">{doc.notes}</p></div>}
      {doc.terms_and_conditions && <div className="mt-6"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Terms &amp; Conditions</div><p className="mt-1 whitespace-pre-wrap text-xs text-slate-500">{doc.terms_and_conditions}</p></div>}
    </div>
  );
}

function TermsModal({ title, q, isRevise, onClose, onSubmit }) {
  const [form, setForm] = useState({
    costing_version: "", currency: q.currency, fx_rate: q.fx_rate,
    valid_until: q.valid_until || "", notes: q.notes || "", internal_notes: q.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const isIdr = (form.currency || "IDR").toUpperCase() === "IDR";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { currency: form.currency.toUpperCase(), fx_rate: isIdr ? 1 : Number(form.fx_rate) || 0 };
      if (form.valid_until) payload.valid_until = form.valid_until;
      if (form.notes.trim()) payload.notes = form.notes.trim();
      if (!isRevise && form.internal_notes.trim()) payload.internal_notes = form.internal_notes.trim();
      if (isRevise && form.costing_version) payload.costing_version = Number(form.costing_version);
      await onSubmit(payload);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={isRevise ? "revise-modal" : "edit-quotation-modal"}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">{title}</h3>
          <button onClick={onClose} data-testid="terms-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          {isRevise && (
            <div>
              <label className={LABEL}>Costing version (blank = latest)</label>
              <input type="number" min="1" value={form.costing_version} onChange={set("costing_version")} placeholder={`Current: v${q.costing_version}`} data-testid="revise-costing-version-input" className={INPUT} />
              <p className="mt-1 text-xs text-slate-400">The new revision snapshots this exact costing version. The quotation number is kept.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><label className={LABEL}>Currency (ISO)</label><input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} maxLength={3} data-testid="terms-currency-input" className={`${INPUT} font-mono-tf uppercase`} /></div>
            <div><label className={LABEL}>FX rate (per 1 unit)</label><input disabled={isIdr} type="number" value={isIdr ? 1 : form.fx_rate} onChange={set("fx_rate")} data-testid="terms-fx-input" className={`${INPUT} disabled:opacity-40`} /></div>
          </div>
          <div><label className={LABEL}>Valid until</label><input type="date" value={form.valid_until} onChange={set("valid_until")} data-testid="terms-valid-until-input" className={INPUT} /></div>
          <div><label className={LABEL}>Customer notes</label><textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="terms-notes-input" className={INPUT} /></div>
          {!isRevise && <div><label className={LABEL}>Internal notes</label><textarea rows={2} value={form.internal_notes} onChange={set("internal_notes")} data-testid="terms-internal-notes-input" className={INPUT} /></div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="terms-modal-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isRevise ? <History className="h-4 w-4" /> : <Pencil className="h-4 w-4" />} {isRevise ? "Create revision" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RejectModal({ onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(reason.trim()); } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">Reject Quotation</h3>
          <button onClick={onClose} data-testid="reject-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div><label className={LABEL}>Reason (optional)</label><textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" className={INPUT} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="reject-modal-save" className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Reject
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
